import { useMemo, useState } from 'react';
import { Search, ArrowUpDown } from 'lucide-react';
import { Input } from './ui/input';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from './ui/select';
import { InventoryColumnSelector } from './InventoryColumnSelector';

const SORTS = [
  ['item_name', 'Name'], ['sku', 'Internal SKU'], ['manufacturer_part_number', 'Mfr Part #'],
  ['qty_on_hand', 'On hand'], ['available_qty', 'Available'], ['quarantined_qty', 'Quarantined'],
];
const WAREHOUSE_SORTS = [...SORTS.slice(0, 5), ['allocated_qty', 'Allocated'], ['reserved_qty', 'Reserved'], SORTS[5]];

const SortSelect = ({ sorts, viewState, onChange, className, testId }) => (
  <Select value={`${viewState.sortKey}:${viewState.sortDir}`} onValueChange={v => { const [k, d] = v.split(':'); viewState.setSortExact(k, d); onChange?.(); }}>
    <SelectTrigger className={className} data-testid={testId} aria-label="Sort inventory">
      <ArrowUpDown className="w-3.5 h-3.5 mr-1.5 shrink-0" /><SelectValue />
    </SelectTrigger>
    <SelectContent>
      {sorts.flatMap(([k, l]) => [
        <SelectItem key={`${k}:asc`} value={`${k}:asc`}>{l} ↑</SelectItem>,
        <SelectItem key={`${k}:desc`} value={`${k}:desc`}>{l} ↓</SelectItem>,
      ])}
    </SelectContent>
  </Select>
);

// Phones hide most table headers, so sorting is offered as a compact select there.
export const MobileSortSelect = ({ viewState, onChange }) => (
  <div className="sm:hidden">
    <SortSelect sorts={WAREHOUSE_SORTS} viewState={viewState} onChange={onChange} className="h-9 w-[150px] text-xs" testId="warehouse-mobile-sort" />
  </div>
);
const norm = (v) => (v || '').toLowerCase().replace(/[^a-z0-9]/g, '');
const FIELD = { item_name: 'item_name', sku: 'item_sku', manufacturer_part_number: 'item_manufacturer_part_number' };

// Client-side search/sort over the vehicle's full inventory (the API returns every row, unpaginated).
export function useVehicleInventoryFilter(inventory, view) {
  const [query, setQuery] = useState('');
  const rows = useMemo(() => {
    const q = query.trim().toLowerCase();
    const nq = norm(query);
    const hit = (inv) => !q || [inv.item_name, inv.item_sku, inv.item_manufacturer_part_number].some(v => (v || '').toLowerCase().includes(q))
      || (nq.length >= 3 && [inv.item_sku, inv.item_manufacturer_part_number].some(v => norm(v).includes(nq)));
    const key = view.sortKey;
    const val = (inv) => (FIELD[key] ? (key === 'item_name' ? (inv.item_name || '').toLowerCase() : norm(inv[FIELD[key]])) || null : Number(inv[key] ?? 0));
    const dir = view.sortDir === 'desc' ? -1 : 1;
    return inventory.filter(hit).sort((a, b) => {
      const x = val(a), y = val(b);
      if (x === null || y === null) return x === y ? 0 : x === null ? 1 : -1;
      return (x < y ? -1 : x > y ? 1 : 0) * dir || (a.item_name || '').localeCompare(b.item_name || '');
    });
  }, [inventory, query, view.sortKey, view.sortDir]);
  return { rows, query, setQuery };
}

export const VehicleInventoryToolbar = ({ filter, view }) => (
  <div className="flex flex-wrap items-center gap-2 px-4 pb-3" data-testid="vehicle-inventory-toolbar">
    <div className="relative flex-1 min-w-[180px]">
      <Search className="w-4 h-4 absolute left-2.5 top-1/2 -translate-y-1/2 text-muted-foreground" />
      <Input value={filter.query} onChange={e => filter.setQuery(e.target.value)} placeholder="Search name, SKU or MPN…"
        className="h-9 pl-8" data-testid="vehicle-inventory-search" aria-label="Search vehicle inventory" />
    </div>
    <SortSelect sorts={SORTS} viewState={view} className="h-9 w-[190px]" testId="vehicle-inventory-sort" />
    <InventoryColumnSelector view="vehicle" viewState={view} />
  </div>
);
